import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, Receipt, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No payment session found');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const res = await axios.get(`${API}/api/payments/status/${sessionId}`, { headers });
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(res.data);
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else if (pollCount < 5) {
          // Continue polling
          setPollCount(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('pending');
          setPaymentData(res.data);
        }
      } catch (err) {
        console.error('Payment status check error:', err);
        setStatus('error');
        setError('Failed to verify payment status');
      }
    };

    checkPaymentStatus();
  }, [sessionId, pollCount]);

  const formatAmount = (amount, currency = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verifying Payment</h1>
              <p className="text-slate-500">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-emerald-700 mb-2">Payment Successful!</h1>
              <p className="text-slate-500 mb-6">Thank you for your payment.</p>
              
              {paymentData && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-500">Amount Paid</span>
                    <span className="font-bold text-lg">
                      {formatAmount(paymentData.amount_total, paymentData.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Status</span>
                    <span className="text-emerald-600 font-medium">Paid</span>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/invoices')}
                  className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  View Invoices
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-amber-700 mb-2">Payment Processing</h1>
              <p className="text-slate-500 mb-6">Your payment is being processed. This may take a few moments.</p>
              <Button 
                variant="outline"
                onClick={() => navigate('/invoices')}
                className="w-full"
              >
                Check Invoice Status
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-slate-100 rounded-full flex items-center justify-center">
                <XCircle className="h-10 w-10 text-slate-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-700 mb-2">Session Expired</h1>
              <p className="text-slate-500 mb-6">This payment session has expired. Please try again.</p>
              <Button 
                onClick={() => navigate('/invoices')}
                className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
              >
                Return to Invoices
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-red-700 mb-2">Payment Error</h1>
              <p className="text-slate-500 mb-6">{error || 'An error occurred during payment verification.'}</p>
              <Button 
                onClick={() => navigate('/invoices')}
                className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
              >
                Return to Invoices
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
